import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  ListItemText,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import PromptDialog from "@entrilia/shared/components/dialog/PromptDialog.tsx";
import PromptDialogHeader from "@entrilia/shared/components/dialog/PromptDialogHeader.tsx";
import DialogButtons from "@entrilia/shared/components/DialogButtons.tsx";
import TextFieldExt from "@entrilia/shared/components/inputs/TextFieldExt.tsx";
import LoaderBox from "@entrilia/shared/components/LoaderBox.tsx";
import { MenuItemStyled } from "@entrilia/shared/components/MenuItemStyled.tsx";
import useDebounce from "@entrilia/shared/hooks/useDebounce.ts";
import useFetch from "@entrilia/shared/hooks/useFetch.ts";
import { logError } from "@entrilia/shared/logging.ts";
import type { ValidationResult } from "@entrilia/shared/utilities/validators.ts";
import { validResult } from "@entrilia/shared/utilities/validators.ts";
import biApi from "../../../../../api/biApi.ts";
import type {
  ClientInfoExtended,
  ReportConfiguration,
  ReportGroup,
  ReportTemplate,
} from "../../../../../api/types/svTypes.ts";
import { FundType } from "../../../../../api/types/svTypes.ts";
import DataSource from "./DataSources.tsx";
import { formatFundType } from "./helpers/reportTemplateHelper.ts";
import { FundTypeValidator, MainInfoValidator, TemplateDescriptionValidator } from "./SaveTemplateDialog.validator.ts";

export interface SaveTemplateDialogProps {
  template: ReportTemplate;
  title: string;
  companies: ClientInfoExtended[];
  onClose: () => void;
  onSaved: (template: ReportTemplate, dataSourceChanged: boolean) => void;
  getTemplateConfigurationAsync: () => Promise<ReportConfiguration | undefined>;
}

export default function SaveTemplateDialog({
  template,
  title,
  companies,
  onClose,
  onSaved,
  getTemplateConfigurationAsync,
}: SaveTemplateDialogProps) {
  const [error, setError] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const [templateToSave, setTemplateToSave] = useState(() => {
    return {
      ...template,
      dataSource: companies.find((c) => c.clientCode === template.dataSource)
        ? template.dataSource
        : companies[0]?.clientCode || "",
    };
  });
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  const fetchGroups = useCallback(
    () => biApi.reportTemplateGroup.getAccessibleReportGroups(template.organization),
    [template.organization]
  );

  const [groups, groupsError, { isFetching: isFetchingGroups }] = useFetch(fetchGroups);

  const validateTemplateNameAsync = useCallback(async () => {
    setIsLoading(true);
    try {
      const templateId = !template.reportId ? undefined : template.reportId;
      const result = await biApi.validateTemplateName({
        templateId: templateId,
        name: templateToSave.name.trim(),
        organization: template.organization,
      });
      if (!result.success || result.error) {
        setError("An error occurred while renaming the template.");
        return false;
      }
      if (result.success && result.data.nameExists === true) {
        setError("A template with this name already exists");
        return false;
      }
      return true;
    } catch (e) {
      logError(e, "[SaveTemplateDialog] validateTemplateName");
      setError("An error occurred while renaming the template.");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [template.reportId, template.organization, templateToSave.name]);

  const save = useCallback(
    async (newTemplate: ReportTemplate) => {
      setIsLoading(true);

      const trimmedTemplate = { ...newTemplate, name: newTemplate.name.trim() };
      try {
        const response = await biApi.updateReportTemplate({ template: trimmedTemplate });
        if (response.success && response.data !== undefined) {
          onSaved(response.data, trimmedTemplate.dataSource !== template.dataSource);
        } else {
          setError("An error occurred while saving the template.");
        }
      } catch {
        setError("An error occurred while saving the template.");
      } finally {
        setIsLoading(false);
      }
    },
    //eslint-disable-next-line @eslint-react/exhaustive-deps
    [template.dataSource, template.reportId]
  );

  const handleSave = useCallback(
    async (template: ReportTemplate) => {
      if (isFormValid && (await validateTemplateNameAsync())) {
        const templateConfiguration = await getTemplateConfigurationAsync();
        if (templateConfiguration !== undefined) {
          template.configuration = templateConfiguration;
        }
        await save(template);
      }
    },
    [validateTemplateNameAsync, isFormValid, getTemplateConfigurationAsync, save]
  );

  useEffect(() => {
    // TODO #209
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setIsFormValid(
      MainInfoValidator(templateToSave.name).isValid &&
        FundTypeValidator(templateToSave.fundTypes).isValid &&
        TemplateDescriptionValidator(templateToSave.description).isValid
    );
  }, [templateToSave.name, templateToSave.fundTypes, templateToSave.description]);

  useEffect(() => {
    const groupId = groups?.[0]?.id;
    if (groups !== undefined && groups.length > 0 && !templateToSave.groupId && groupId) {
      // TODO #209
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setTemplateToSave((prev) => ({ ...prev, groupId }));
    }
  }, [groups, templateToSave.groupId]);

  return (
    <PromptDialog open={true} sx={{ minWidth: 600, height: "100%", maxHeight: 735, p: 0 }}>
      <PromptDialogHeader text={title} disabled={isLoading} onClose={onClose} />
      <Divider />
      {error && (
        <Alert sx={{ mt: 2, mx: 3 }} severity="error">
          {error}
        </Alert>
      )}
      {groupsError && (
        <Alert sx={{ mt: 2, mx: 3 }} severity="error">
          An error occurred while fetching the data for the template.
        </Alert>
      )}
      <Stack sx={{ gap: 2, pl: 3, pr: 1, py: 2, flex: 1, overflowY: "scroll" }}>
        <LoaderBox loading={isFetchingGroups}>
          <MainInfoBlock
            templateName={templateToSave.name}
            saving={isLoading}
            onSetName={(name) => setTemplateToSave((prev) => ({ ...prev, name: name }))}
            groupId={templateToSave.groupId ?? ""}
            onSetGroupId={(groupId) => setTemplateToSave((prev) => ({ ...prev, groupId: groupId }))}
            groups={groups || []}
          />
          <FundTypeBlock
            selectedTypes={templateToSave.fundTypes}
            onChange={(types) => setTemplateToSave((prev) => ({ ...prev, fundTypes: types }))}
          />
          <DataSource
            title="Default Data Source"
            disclaimer="The data provided by this company will be used to demonstrate the report template."
            dataSource={templateToSave.dataSource}
            companies={companies}
            onChange={(company) => setTemplateToSave((prev) => ({ ...prev, dataSource: company }))}
            isLoading={isLoading}
          />
          <TemplateDescriptionBlock
            description={templateToSave.description}
            onChange={(value) => setTemplateToSave((prev) => ({ ...prev, description: value }))}
          />
        </LoaderBox>
      </Stack>
      <Divider />

      <DialogButtons onCancel={onClose}>
        <Button
          variant="contained"
          disabled={isLoading || !isFormValid || isFetchingGroups}
          loading={isLoading}
          onClick={() => handleSave(templateToSave)}
        >
          Save
        </Button>
      </DialogButtons>
    </PromptDialog>
  );
}

interface TemplateDescriptionBlockProps {
  description: string;
  disabled?: boolean;
  onChange: (description: string) => void;
}

function TemplateDescriptionBlock({ description, onChange, disabled }: TemplateDescriptionBlockProps) {
  const debouncedDescription = useDebounce((v: string) => onChange(v), 300);
  return (
    <Stack sx={{ gap: 2 }}>
      <Stack sx={{ gap: 1 }}>
        <Typography variant="subtitle2">Template Description</Typography>
      </Stack>
      <TextFieldExt
        name="description"
        textFieldProps={{
          size: "small",
          label: "Description",
          variant: "outlined",
          placeholder: "Add description to template...",
          disabled: disabled,
          multiline: true,
          rows: 8,
          defaultValue: description,
        }}
        doValidate={TemplateDescriptionValidator}
        onValidated={(_, result) => result.error}
        onValueChanged={debouncedDescription}
      />
    </Stack>
  );
}

interface FundTypeBlockProps {
  selectedTypes: FundType[];
  onChange: (types: FundType[]) => void;
}

function FundTypeBlock({ selectedTypes, onChange }: FundTypeBlockProps) {
  const [validationResult, setValidationResult] = useState<ValidationResult>(validResult);
  const valueChanged = () => {
    const validationResult = FundTypeValidator(selectedTypes);
    setValidationResult(validationResult);
  };

  return (
    <Stack sx={{ gap: 2 }}>
      <Stack sx={{ gap: 1 }}>
        <Typography variant="subtitle2">
          Fund Type
          <Typography variant="caption" sx={{ color: (theme) => theme.palette.error.main }}>
            *
          </Typography>
        </Typography>
        <Typography>Choose the fund types for which this report template will be applicable.</Typography>
      </Stack>
      <Autocomplete
        multiple
        disablePortal
        value={selectedTypes || []}
        options={Object.keys(FundType) as FundType[]}
        disableCloseOnSelect
        getOptionLabel={(option) => option}
        renderValue={(selectedOpts, getTagProps) =>
          selectedOpts.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                variant="outlined"
                label={<Typography>{formatFundType(option)}</Typography>}
                key={key}
                {...tagProps}
                sx={{ borderColor: "divider" }}
              />
            );
          })
        }
        renderOption={({ key: _key, ...props }, option, { selected }) => (
          <Box component="li" key={option} {...props}>
            <Checkbox checked={selected} />
            <ListItemText primary={formatFundType(option)} />
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                sx: { gap: 1 },
                readOnly: true,
              },
            }}
            onBlur={() => valueChanged()}
            aria-readonly={"true"}
            error={!validationResult.isValid}
            helperText={validationResult.error}
          />
        )}
        onChange={(_, newValue) => {
          onChange(newValue);
        }}
        sx={{ ".MuiAutocomplete-tag": { m: 0 } }}
      />
    </Stack>
  );
}

interface MainInfoBlockProps {
  templateName: string;
  groupId: string;
  groups: ReportGroup[];
  saving: boolean;
  onSetName: (name: string) => void;
  onSetGroupId: (groupId: string) => void;
}

function MainInfoBlock({ templateName, saving, onSetName, groupId, onSetGroupId, groups }: MainInfoBlockProps) {
  return (
    <Stack sx={{ gap: 2.5 }}>
      <Typography variant="subtitle2">
        Main Info
        <Typography variant="caption" sx={{ color: (theme) => theme.palette.error.main }}>
          *
        </Typography>
      </Typography>
      <TextFieldExt
        name="name"
        textFieldProps={{
          size: "small",
          label: "Name",
          variant: "outlined",
          value: templateName,
          sx: { flex: 1 },
          placeholder: "Set Name...",
          disabled: saving,
        }}
        doValidate={MainInfoValidator}
        onValidated={(_, result) => result}
        onValueChanged={(value) => onSetName(value)}
      />
      <FormControl fullWidth disabled={saving}>
        <InputLabel>Group name</InputLabel>
        <Select
          value={groups.length > 0 ? groupId : ""}
          label="Group name"
          onChange={(evt) => onSetGroupId(evt.target.value)}
        >
          {groups.map((rg) => {
            return (
              <MenuItemStyled key={rg.id} value={rg.id}>
                <Typography>{rg.caption}</Typography>
              </MenuItemStyled>
            );
          })}
        </Select>
      </FormControl>
    </Stack>
  );
}
